import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Spinner from "react-bootstrap/Spinner";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { HslStringColorPicker } from "react-colorful";
import {
  errorEdit,
  successEdit,
  loadingEdit,
} from "../../../redux/slices/status.slice";
import styles from "./index.module.scss";

const DEFAULT_COLOR = "hsl(175, 90%, 75%)";

function StyleForm() {
  const dispatch = useDispatch();
  const { loading, error, success } = useSelector((state) => state.status);
  const { chosenStyle } = useSelector((state) => state.styles);
  const { isAdmin, id } = useSelector((state) => state.personality.user);
  const { chosenUser } = useSelector((state) => state.users);

  const [action, setAction] = useState("STYLE_ADD_ONE");
  const [clickDeletItem, setClickDeletItem] = useState(false);

  const [colorHex, changeColorHook] = useState(
    chosenStyle?.styleColor || DEFAULT_COLOR
  );
  const [activeColor, setActiveColor] = useState(true); // default color always pre-applied
  const [errorColor, setErrorColor] = useState(null);

  const schema = yup
    .object({
      styleName: yup
        .string()
        .required("This field is required")
        .min(2, "Name is too short - should be 2 chars minimum."),
    })
    .required();

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      styleName: chosenStyle?.styleName || "",
    },
  });

  useEffect(() => {
    if (chosenStyle) {
      reset({
        styleName: chosenStyle.styleName || "",
      });
      changeColorHook(chosenStyle.styleColor || DEFAULT_COLOR);
      setActiveColor(true);
    } else {
      reset({ styleName: "" });
      changeColorHook(DEFAULT_COLOR);
      setActiveColor(true); // reset to default color state
    }
  }, [chosenStyle, reset]);

  useEffect(() => {
    return () => {
      clearErrors();
      reset();
      dispatch(successEdit(null));
      dispatch(errorEdit(null));
      dispatch(loadingEdit(false));
      setErrorColor(null);
    };
  }, [clearErrors, reset, dispatch]);

  useEffect(() => {
    if (success) {
      reset();
      changeColorHook(DEFAULT_COLOR);
      setActiveColor(true); // reset to default color state
    }
  }, [success, reset]);

  const onSubmit = (value) => {
    if (!activeColor) {
      setErrorColor("This field is required");
      return;
    }

    setErrorColor(null);
    dispatch(successEdit(null));
    dispatch(errorEdit(null));
    dispatch(loadingEdit(false));

    if (action === "STYLE_REMOVE_ONE" && !clickDeletItem) {
      setClickDeletItem(true);
      return;
    }

    const payload = {
      userId: isAdmin ? chosenUser.id : id,
      styleName: value.styleName,
      styleId: chosenStyle?.id || "",
      styleColor: colorHex,
    };

    dispatch({ type: action, payload });
  };

  return (
    <>
      {loading ? (
        <div className={styles.spinnerWrapper}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          {clickDeletItem && (
            <div className={styles.confirm}>
              Are you sure you want to delete this Style?
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            {!clickDeletItem && (
              <>
                <div className={styles.formAnswer}>
                  {error && <div className="text-danger">{error}</div>}
                </div>

                <label>Style</label>
                <input {...register("styleName")} />
                {errors.styleName && (
                  <div className={styles.formAnswer}>
                    <div className="text-danger">
                      {errors.styleName.message}
                    </div>
                  </div>
                )}

                <label>Color</label>
                <div className={styles.colorPicker}>
                  <HslStringColorPicker
                    onClick={() => setActiveColor(true)}
                    color={colorHex}
                    onChange={(color) => {
                      changeColorHook(color);
                      setActiveColor(true);
                    }}
                  />
                  <div
                    style={{ background: colorHex }}
                    className={styles.color}
                  ></div>
                </div>

                {errorColor && (
                  <div className={styles.formAnswer}>
                    <div className="text-danger">{errorColor}</div>
                  </div>
                )}
              </>
            )}

            {!chosenStyle ? (
              <input
                type="submit"
                className={styles.submit}
                value="Save"
                onClick={() => setAction("STYLE_ADD_ONE")}
              />
            ) : (
              <div className={styles.buttons}>
                {clickDeletItem ? (
                  <>
                    <button
                      className={styles.submitUpdate}
                      onClick={() => setClickDeletItem(false)}
                    >
                      No
                    </button>
                    <input
                      type="submit"
                      className={styles.submitDelete}
                      value="Yes"
                    />
                  </>
                ) : (
                  <>
                    <input
                      type="submit"
                      className={styles.submitDelete}
                      value="Delete"
                      onClick={() => setAction("STYLE_REMOVE_ONE")}
                    />
                    <input
                      type="submit"
                      className={styles.submitUpdate}
                      value="Save"
                      onClick={() => setAction("STYLE_UPDATE_ONE")}
                    />
                  </>
                )}
              </div>
            )}
          </form>
        </>
      )}
    </>
  );
}

export default StyleForm;
